import globalStore from '@/services/global.state';
import theme from '@/style';
import React, {useCallback, useState, useContext} from 'react';
import {RefreshControl, ScrollView, StatusBar, View} from 'react-native';
import HomeHeader from './components/home-header';
import DownloadBanner from './components/home-download-banner';
import HomeBanner from './components/home-banner-v2';
import HomeGoldArea from './components/home-gold-area';
import HomeFloorGames from './components/home-floor-games';
import HomeWinningInformation from './components/home-winning-information';
import HomeSide from './components/home-side';
import {getHomeGames} from './home.service';
import {
  HomeGameCardItem,
  HomeGameFloorItem,
  IHomeSportsCardItem,
} from './home.type';
import {useFocusEffect} from '@react-navigation/native';
// import {useInnerStyle} from './home.hooks';
import {FadeInView} from '@/components/basic/animations';
import {useResponsiveDimensions} from '@/utils';
import HomeFloorLottery from './components/home-floor-lottery';
import HomePartners from './components/home-partners';
import HomePays from './components/home-pays';
import HomeSocial from './components/home-social';
import HomeLicenses from './components/home-licenses';
import {Image} from 'antd-mobile';
import DownImg from '@assets/imgs/home/down-img.webp';
import {useTranslation} from 'react-i18next';
import {downloadApk, stopBeforeDownload} from '@/utils';
import {sportsIconMap, toSportItem} from '../gr8-sports/converts';
import HomeFloorScratch from './components/home-floor-scratch';
import SportFloor from '../gr8-sports/components/sport-floor';
import {VipUpgradeContext} from '@/common-pages/vip/provider/vip-upgrade-provider';
import {sequentialStorage} from '@utils/Storage/SequentialStorage';

const Home = () => {
  const {i18n} = useTranslation();
  const [show, setShow] = useState(false);
  const [homeGames, setHomeGames] = useState<HomeGameFloorItem[]>();
  const [refreshing, setRefreshing] = useState(false);
  const [version, setVersion] = useState(0);

  const vipUpgradeProvider = useContext(VipUpgradeContext);

  React.useEffect(() => {
    StatusBar.setBackgroundColor(theme.basicColor.primary);
    StatusBar.setBarStyle('dark-content');
    init();
  }, []);

  useFocusEffect(
    useCallback(() => {
      vipUpgradeProvider.showVipUpgrade();
    }, [vipUpgradeProvider]),
  );
  const init = (loading: boolean = true) => {
    loading && globalStore.globalLoading.next(true);
    return getHomeGames()
      .then(games => {
        setHomeGames(games);
      })
      .finally(() => globalStore.globalLoading.next(false));
  };
  const handleFocusEffect = useCallback(() => {
    const sub = globalStore.tokenSubject.subscribe(token => {
      if (token) {
        globalStore.amountCheckOut.next({});
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }, []);
  useFocusEffect(handleFocusEffect);
  const handleRefresh = () => {
    setRefreshing(true);
    setVersion(_v => _v + 1);
    Promise.allSettled([init(false)]).finally(() => setRefreshing(false));
  };
  const {width: screenWidth} = useResponsiveDimensions();
  const onClickDownloadBtn = () => {
    if (stopBeforeDownload()) {
      return;
    }
    downloadApk();
  };
  const handleLink = (floor: HomeGameFloorItem) => {
    if (floor.uniqueKey === 'HotMatches') {
      return globalStore.openSports.next('/cricket');
    }
    if (floor.uniqueKey === 'TableTennis') {
      globalStore.openSports.next('/table-tennis/live');
      return;
    }
    if (floor.uniqueKey === 'CyberSport') {
      globalStore.openSports.next('/e-sports/live');
      return;
    }
    globalStore.openSports.next(`/${floor.uniqueKey.toLowerCase()}/live`);
  };
  useFocusEffect(
    useCallback(() => {
      console.log('focus');
      sequentialStorage.emit('updated');
    }, []),
  );
  return (
    <FadeInView
      style={[
        theme.fill.fill,
        {
          minWidth: screenWidth,
        },
        theme.flex.col,
      ]}>
      <HomeHeader />
      {globalStore.isWeb ? <DownloadBanner /> : null}
      <ScrollView
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }>
        <div className={'bg-bg'}>
          <View style={{height: theme.paddingSize.l}} />
          <HomeBanner version={version} />
          <HomeGoldArea />
          {homeGames?.map((floor, index) => {
            // 这一块不是体育楼层
            if (!floor.tagName || !floor.tagName.endsWith('_sport')) {
              const gamesList = (floor.gamesList as HomeGameCardItem[]) || [];
              if (floor.uniqueKey === 'lotteryV2') {
                return (
                  <HomeFloorLottery
                    key={index}
                    title={floor.name}
                    icon={floor.categoryPic}
                  />
                );
              }
              if (floor.uniqueKey === 'scratchV2') {
                return (
                  <HomeFloorScratch
                    key={index}
                    title={floor.name}
                    icon={floor.categoryPic}
                    imageUrl={gamesList?.[0]?.gamePic}
                  />
                );
              }
              return floor.gamesList ? (
                <HomeFloorGames
                  type={floor.uniqueKey}
                  key={index}
                  title={floor.name}
                  icon={floor.categoryPic}
                  list={gamesList || []}
                />
              ) : null;
            }
            // 确定是体育内容，判断gamesList存在的情况
            if (!floor.gamesList?.length) {
              return null;
            }
            try {
              const gamesList = floor.gamesList as IHomeSportsCardItem[];
              const sport = floor.tagName?.split('_')[0];
              const isSport =
                gamesList[0].grSportName.toLowerCase() === sport.toLowerCase();
              const isHotOrPremierLeague =
                floor.uniqueKey === 'HotMatches' ||
                floor.uniqueKey === 'PremierLeague' ||
                floor.uniqueKey === 'Premier League';
              return (
                <SportFloor
                  key={index}
                  title={floor.name}
                  icon={floor.categoryPic}
                  sportsList={gamesList.map(game => toSportItem(game))}
                  hasLink={isSport || isHotOrPremierLeague}
                  hasLiveIcon={isSport}
                  onLinkClick={() => handleLink(floor)}
                  isSport={isSport}
                  defaultSportIcon={
                    floor.categoryPic ||
                    sportsIconMap[sport as keyof typeof sportsIconMap] ||
                    undefined
                  }
                />
              );
            } catch (error) {
              // 体育楼层数据异常，不显示
              console.log(error);
              return null;
            }
          })}
          <HomeWinningInformation version={version} />
        </div>
        <div className="w-full bg-white">
          <HomePartners />
          <div className="h-[0.0625rem]" />
          <HomePays />
          <div className="h-[0.0625rem]" />
          <HomeSocial />
          <div className="h-[0.0625rem]" />
          <HomeLicenses />
        </div>
        {/* <View
          style={[
            theme.fill.fillW,
            spaceAreaStyles.bottom,
            {backgroundColor: '#fff'},
          ]}
        /> */}
      </ScrollView>
      <HomeSide />
      {show && (
        <div className="fixed bottom-[5.125rem] w-[23.4375rem] m-auto flex flex-row justify-center">
          <div
            className="flex py-2 px-[0.875rem] items-center gap-2 rounded-[2.75rem] bg-[--1]"
            style={{
              boxShadow: '0px 1px 12px 0px rgba(0, 0, 0, 0.25)',
            }}>
            <div className="w-[1.75rem] h-[1.75rem]">
              <Image src={DownImg} className="w-[1.75rem] h-[1.75rem]" />
            </div>
            <span
              className="text-white text-sm font-bold leading-[66.857%]"
              onClick={() => {
                onClickDownloadBtn();
                setShow(false);
              }}
              style={{
                textShadow: '0px 0.78px 0.39px rgba(71, 129, 255, 0.50)',
              }}>
              {i18n.t('home.label.downText')}
            </span>
            <div className="w-[0.0625rem] h-6 bg-white opacity-50" />
            <div onClick={() => setShow(false)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="18"
                viewBox="0 0 19 18"
                fill="none">
                <rect
                  x="6.39258"
                  y="15.3203"
                  width="3"
                  height="13.3333"
                  rx="1.5"
                  transform="rotate(-135 6.39258 15.3203)"
                  fill="white"
                />
                <rect
                  x="15.584"
                  y="13.1992"
                  width="3"
                  height="13.3333"
                  rx="1.5"
                  transform="rotate(135 15.584 13.1992)"
                  fill="white"
                />
              </svg>
            </div>
          </div>
        </div>
      )}
    </FadeInView>
  );
};
export default Home;
